import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import heroBg from "@/assets/fremside.webp.asset.json";
import { useLanguage, translations } from "@/i18n/LanguageContext";
import VideoModal from "./VideoModal";
import { wasPrerendered } from "@/lib/prerender";

const HeroSection = () => {
  const { t } = useLanguage();
  const [videoOpen, setVideoOpen] = useState(false);
  const [showBgVideo, setShowBgVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [skipIntro] = useState(wasPrerendered);


  // Mount the heavy Vimeo player only after the page has loaded and the user
  // interacts (or after a few idle seconds). Skipped for prerender, bots and data-saver.
  useEffect(() => {
    const ua = navigator.userAgent;
    if (/ReactSnap|bot|crawler|spider/i.test(ua)) return;
    const conn = (navigator as any).connection;
    if (conn?.saveData || /(^|-)2g$|^3g$/.test(conn?.effectiveType || "")) return;

    let done = false;
    let timer: number | undefined;
    const start = () => {
      if (done) return;
      done = true;
      setShowBgVideo(true);
      cleanup();
    };
    const events = ["pointerdown", "touchstart", "scroll", "keydown", "mousemove"];
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, start));
      if (timer) window.clearTimeout(timer);
    };
    const arm = () => {
      events.forEach((e) => window.addEventListener(e, start, { once: true, passive: true }));
      // On phones, wait for interaction only (saves mobile data); desktop also starts after 4s idle.
      if (window.innerWidth >= 768) timer = window.setTimeout(start, 4000);
    };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    return () => {
      window.removeEventListener("load", arm);
      cleanup();
    };
  }, []);

  return (
    <>
      <section className="relative w-full pt-24 pb-6 px-4 md:px-6 bg-background">
        <div className="relative h-[85vh] md:h-[90vh] w-full overflow-hidden rounded-3xl border border-border/50">
          {/* Background video with image fallback/poster */}
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={heroBg.url}
              alt="Envir industrielt anlegg"
              className="absolute inset-0 w-full h-full object-cover"
              {...({ fetchpriority: "high" } as any)}
            />
            {showBgVideo && (
              <div
                className={`absolute inset-0 pointer-events-none overflow-hidden transition-opacity duration-700 ${videoReady ? "opacity-100" : "opacity-0"}`}
                aria-hidden="true"
              >
                <iframe
                  src="https://player.vimeo.com/video/1205434986?background=1&autoplay=1&loop=1&muted=1&autopause=0&dnt=1"
                  title="Envir bakgrunnsfilm"
                  allow="autoplay; fullscreen"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full border-0"
                  loading="lazy"
                  onLoad={() => setVideoReady(true)}
                />
              </div>
            )}

            {/* Colour overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/80 via-foreground/50 to-foreground/20" />
            {/* Green brand tint – keeps the hero green even if the video is blocked */}
            <div className="absolute inset-0 bg-primary/15" />

          </div>


          {/* Content */}
          <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-16 lg:p-20">
            <div className="max-w-4xl">
              <motion.h1
                initial={skipIntro ? false : { opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
                className="heading-xl text-primary-foreground mb-6"
                dangerouslySetInnerHTML={{ __html: t(translations.hero.title).replace(/\u00AD/g, '&shy;') }}
              />

              <motion.p
                initial={skipIntro ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="text-lg font-body text-primary-foreground/80 max-w-xl mb-8"
              >
                {t(translations.hero.subtitle)}
              </motion.p>

              <motion.div
                initial={skipIntro ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="flex flex-wrap items-center gap-3 md:gap-6"
              >
                <a
                  href="/mottak"
                  className="button-primary"
                >
                  {t(translations.hero.cta1)}
                </a>
                <a
                  href="/gjenvunne-masser"
                  className="button-outline"
                >
                  {t(translations.hero.cta2)}
                </a>
                <button
                  onClick={() => setVideoOpen(true)}
                  className="inline-flex items-center gap-3 text-sm font-body font-500 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                >
                  <Play className="w-5 h-5" />
                  <span className="hidden sm:inline">{t(translations.hero.videoLink)}</span>
                  <span className="sm:hidden">{t({ no: "Film", en: "Video", de: "Film" })}</span>
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </>
  );
};

export default HeroSection;
